import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './dropdown.module.scss';

const VIEWPORT_PADDING = 16;
const DROPDOWN_TOP_OFFSET = 15;
const FALLBACK_TOP_OFFSET = 40;

export default function Dropdown({
  items,
  isOpen,
  onMouseEnter,
  onMouseLeave,
  onItemClick,
  onClose,
  triggerId,
  shouldFocusOnOpen = false,
  buttonRef = null,
  isSlimHeader = false,
}) {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!isOpen || !buttonRef) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!dropdownRef.current || !buttonRef) return;

      const triggerRect = buttonRef.getBoundingClientRect();
      const dropdownWidth = dropdownRef.current.offsetWidth;
      const headerEl = buttonRef.closest('header');
      const top = headerEl
        ? headerEl.getBoundingClientRect().bottom + DROPDOWN_TOP_OFFSET
        : triggerRect.bottom + FALLBACK_TOP_OFFSET;

      let left = triggerRect.left - 25;

      const maxLeft = window.innerWidth - dropdownWidth - VIEWPORT_PADDING;
      left = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft));

      setPosition({ left, top });
    };

    const frame = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, buttonRef]);

  useEffect(() => {
    if (isOpen && shouldFocusOnOpen && dropdownRef.current) {
      const firstLink = dropdownRef.current.querySelector('a');
      if (firstLink) {
        firstLink.focus();
      }
    }
  }, [isOpen, shouldFocusOnOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        if (onClose) onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!items?.length) return null;

  const columns = items.filter((section) => section.items?.length);

  let style = {};
  if (position && isOpen) {
    style = { left: `${position.left}px`, top: `${position.top}px` };
  } else if (!position && isOpen) {
    style = { visibility: 'hidden' };
  }

  return (
    <div
      className={classNames(styles.dropdownContainer, {
        [styles.dropdownContainerSlim]: isSlimHeader,
        [styles.dropdownHidden]: !isOpen,
      })}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={dropdownRef}
      role="menu"
      aria-labelledby={triggerId}
      aria-hidden={!position || undefined}
      tabIndex={-1}
    >
      <div className={styles.dropdown}>
        <div className={styles.columnsWrapper}>
          {columns.map((section, index) => (
            <div
              key={`column-${
                section.items?.[0]?.label || section.items?.[0]?.link || index
              }`}
              className={styles.section}
            >
              <ul className={styles.itemList}>
                {section.items?.map((item) => (
                  <li key={item.link || item.label} role="none">
                    <a
                      href={item.link}
                      className={`${styles.item} ${
                        item.highlighted ? styles.highlighted : ''
                      }`}
                      onClick={() => onItemClick && onItemClick(item.label)}
                      role="menuitem"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  items: PropTypes.array,
  isOpen: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onItemClick: PropTypes.func,
  onClose: PropTypes.func,
  triggerId: PropTypes.string,
  shouldFocusOnOpen: PropTypes.bool,
  buttonRef: PropTypes.object,
  isSlimHeader: PropTypes.bool,
};
